import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  deleteToken as deleteFirebaseToken,
  getMessaging,
  getToken,
  isSupported,
} from 'firebase/messaging';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  DevicePlatform,
  DeviceToken,
  useCreateDeviceMutation,
  useDeleteDeviceMutation,
} from '@graphql/generated';
import { useAuthContext } from '@src/context/authContext';
import { SecurityStorage } from '@utils/storage';

import type {
  NotificationContext,
  NotificationProviderProps,
} from './useNotification';
import type { Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY || '',
  authDomain: process.env.FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.FIREBASE_APP_ID || '',
};

const VERSION = '2026-07-13-1';

// Store the config as params to pass to the service worker
const firebaseConfigAsParams = new URLSearchParams(firebaseConfig);

// Lazily initialize the Firebase app on first access so that module evaluation
// never calls getApps() — avoids crashing test suites whose firebase/app mock
// only stubs initializeApp (the common pre-TA-9729 pattern).
let _firebaseApp: ReturnType<typeof getApp> | null = null;
function getFirebaseApp() {
  if (!_firebaseApp) {
    _firebaseApp =
      getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  }
  return _firebaseApp;
}

const defaultNotificaitonContext: NotificationContext = {
  deleteToken: () => Promise.resolve(void 0),
  clearNotificationData: () => void 0,
  registerForPushNotifications: () => void 0,
  registerForPushNotificationsAsync: () => Promise.resolve(undefined),
};

const notificationContext = createContext<NotificationContext>(
  defaultNotificaitonContext
);

export const NotificationProvider = ({
  children,
}: NotificationProviderProps) => {
  const { Provider } = notificationContext;

  const [createDevice] = useCreateDeviceMutation();
  const { token: authToken } = useAuthContext();
  const [notificationData, setNotificationData] =
    useState<NotificationContext['notificationData']>();
  const [messaging, setMessaging] = useState<Messaging | null>(null);

  useEffect(() => {
    (async () => {
      try {
        if (await isSupported()) {
          setMessaging(getMessaging(getFirebaseApp()));
        }
      } catch {
        // isSupported() throws in some environments (e.g. restricted WebViews)
      }
    })();
  }, []);

  // Service-worker message listener — registered once, cleaned up on unmount.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const handleMessage = (
      event: MessageEvent<{
        type: 'LOAD_CHAT' | 'LOAD_PROJECT';
        payload: string;
        messageType: string;
        data?: {
          chat_id?: string;
          message_id?: string;
          project_id?: string;
          action?: string;
        };
      }>
    ) => {
      if ('type' in event.data && event.data.type === 'LOAD_CHAT') {
        setNotificationData({ chatId: event.data.payload });
      }
      if ('type' in event.data && event.data.type === 'LOAD_PROJECT') {
        setNotificationData({ projectUUId: event.data.payload });
      }
      if (event.data.messageType === 'notification-clicked') {
        // Firebase SDK-displayed notifications post this message on click.
        // Project nudges carry action + project_id (a UUID) — TA-10185.
        const data = event.data.data ?? {};
        if (data.action === 'project_task_list' && data.project_id) {
          window.sessionStorage.setItem(
            'notification_project_uuid',
            data.project_id
          );
          setNotificationData({ projectUUId: data.project_id });
          return;
        }
        if (
          data.message_id &&
          data.message_id !==
            window.sessionStorage.getItem('notification_message_id')
        ) {
          data.chat_id &&
            window.sessionStorage.setItem('notification_chat_id', data.chat_id);
          window.sessionStorage.setItem(
            'notification_message_id',
            data.message_id
          );
          data.chat_id &&
            window.sessionStorage.setItem('notification_open_chat', 'true');
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  const clearNotificationData = useCallback(() => {
    setNotificationData(undefined);
  }, []);

  // Lazily initializes messaging if the mount effect hasn't completed yet,
  // so callers that invoke this on their own mount don't always get undefined.
  const registerForPushNotificationsAsync = useCallback(async () => {
    let activeMessaging = messaging;
    if (!activeMessaging) {
      try {
        if (!(await isSupported())) return undefined;
        activeMessaging = getMessaging(getFirebaseApp());
        setMessaging(activeMessaging);
      } catch {
        return undefined;
      }
    }

    // SW registration, the permission prompt and getToken all reject in the
    // wild (messaging/permission-blocked, SW script fetch failures) — treat
    // any of them as "no push on this browser" instead of an unhandled
    // rejection (TA-10846).
    try {
      const serviceWorkerRegistration =
        (typeof navigator !== 'undefined' &&
          'serviceWorker' in navigator &&
          (await navigator.serviceWorker.register(
            `/firebase-messaging-sw.js?v=${VERSION}&${firebaseConfigAsParams}`
          ))) ||
        undefined;

      if (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        Notification.permission !== 'denied'
      ) {
        const permission = await Notification.requestPermission();
        // 'default' (prompt dismissed) would only make getToken throw
        // messaging/permission-blocked, so skip it.
        if (
          permission === 'granted' &&
          activeMessaging &&
          serviceWorkerRegistration
        ) {
          const token = await getToken(activeMessaging, {
            vapidKey: process.env.FIREBASE_VAPID_KEY,
            serviceWorkerRegistration,
          });
          return token;
        }
      }
    } catch (e) {
      console.warn('Web push registration failed:', e);
      return undefined;
    }
  }, [messaging]);

  const registerForPushNotifications = useCallback(() => {
    registerForPushNotificationsAsync()
      .then((pushToken) => {
        if (authToken && pushToken) {
          // Return the mutation so its rejection reaches the catch below.
          return createDevice({
            variables: {
              attributes: {
                token: pushToken,
                tokenType: DeviceToken.Firebase,
                platform: DevicePlatform.Web,
              },
            },
          });
        }
      })
      .catch((e) => {
        console.warn('Web push registration failed:', e);
      });
  }, [registerForPushNotificationsAsync, authToken, createDevice]);

  useEffect(() => {
    if (messaging) {
      registerForPushNotifications();
    }
  }, [messaging, registerForPushNotifications]);

  const [deleteDevice] = useDeleteDeviceMutation();

  const deleteToken = useCallback(async (): Promise<void> => {
    if (!messaging) return;

    if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission !== 'granted'
    ) {
      return;
    }

    const apiToken = (await SecurityStorage.getItem('authToken')) ?? '';

    return getToken(messaging)
      .then(async (token) => {
        if (!token) {
          return;
        }
        return token;
      })
      .then(async (token) => {
        const success = await deleteFirebaseToken(messaging);

        if (success) {
          return token;
        }

        throw new Error('Failed to delete Firebase token');
      })
      .then(async (token) => {
        return deleteDevice({
          variables: { expoToken: token as string, apiToken },
        });
      })
      .catch((err) => {
        throw err instanceof Error ? err : new Error(String(err));
      })
      .then(() => {});
  }, [messaging, deleteDevice]);

  const value = useMemo(
    () => ({
      deleteToken,
      notificationData,
      clearNotificationData,
      registerForPushNotifications,
      registerForPushNotificationsAsync,
    }),
    [
      deleteToken,
      notificationData,
      clearNotificationData,
      registerForPushNotifications,
      registerForPushNotificationsAsync,
    ]
  );

  return <Provider value={value}>{children}</Provider>;
};

export const useNotification = () => {
  const context = useContext(notificationContext);

  if (!context) {
    throw new Error('useNotification must be used within a Provider');
  }

  return context;
};
